<template>
  <div
    class="pw-tab-strip"
    :class="{
      'pw-tab-strip--is-vertical': isVertical,
      'pw-tab-strip--pill-mode': pillMode,
    }"
  >
    <div class="pw-tab-strip__header">
      <ul class="tab-strip__items">
        <li
          v-for="(item, index) in tabItems"
          :key="index"
          @click="tabClick(index)"
          v-show="item.visible"
          :class="{
            'tab-strip__row': true,
            'is-active': activeTab === index,
            'is-disabled': item.disabled,
            'is-filled': item.filled,
            ...classColorActive,
            ...item.customClass,
          }"
        >
          <span v-if="!$slots.title">
            {{ item.title }}
          </span>
          <slot name="title" />
        </li>
      </ul>
    </div>
    <div class="pw-tab-strip__body">
      <slot />
    </div>
  </div>
</template>

<script>
export default {
  name: 'TabStrip',
  props: {
    pillMode: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: '',
    },
    preventChange: {
      type: Function,
      default: () => true,
    },
    tabSelected: Number,
    setSelected: Number,
    isVertical: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tabItems: [],
      isTabs: true,
      activeTab: 0,
    };
  },
  mounted() {
    if (this.setSelected) {
      this.activeTab = this.setSelected;
    }

    if (this.activeTab < this.tabItems.length) {
      this.tabItems[this.activeTab].isActive = true;
    }
  },
  watch: {
    tabSelected(val) {
      if (val !== this.activeTab) {
        this.changeTab(val);
      }
    },
  },
  computed: {
    classColorActive() {
      const d = {};
      const color = this.color.length > 0 ? this.color : this.pillMode ? 'primary' : 'secondary';
      const className = `is-${color}`;
      d[className] = true;
      return d;
    },
  },
  methods: {
    tabClick(index) {
      this.changeTab(index);
    },
    async changeTab(newIndex) {
      const result = await this.preventChange(newIndex, this.activeTab);
      if (!result) {
        return;
      }
      if (this.activeTab === newIndex) {
        return;
      }
      if (this.activeTab < this.tabItems.length) {
        this.tabItems[this.activeTab].deactivate(this.activeTab, newIndex);
      }
      this.tabItems[newIndex].activate(this.activeTab, newIndex);
      this.activeTab = newIndex;
      this.$emit('change', newIndex);
      this.$emit('update:tabSelected', newIndex);
    },
  },
};
</script>

<style lang="scss">
$main: #5186ff;

$primary: #6ae1c8;
$primary-hover: #6ed2bd;
$primary-selected: #51c6ae;
$primary-disabled: #97decf;

$secondary: #5b89f2;
$secondary-hover: #5d86e1;
$secondary-selected: #5079d8;
$secondary-disabled: #8cabf3;

$terciary: #707070;
$terciary-hover: #a7b1b8;
$terciary-selected: #a0a4a7;
$terciary-disabled: #c2c2c2;

$quinary: #f25555;
$quinary-hover: #e14f4f;
$quinary-selected: #e54040;
$quinary-disabled: #ec9696;

$success: #19bf95;
$success-hover: #19bf95;
$success-selected: #19bf95;
$success-disabled: #19bf95;

$color1: #9f9f9f;
$color2: #ffc848;
$color3: #6ae1c8;
$color4: #19bf95;
$color5: #5186ff;
$color6: #707070;
$color7: #f25555;

$list: 'primary', 'secondary', 'terciary', 'quinary', 'success', 'color1', 'color2', 'color3',
  'color4', 'color5', 'color6', 'color7';

$colors: (
  primary: $primary,
  secondary: $secondary,
  terciary: $terciary,
  quinary: $quinary,
  success: $success,
  color1: $color1,
  color2: $color2,
  color3: $color3,
  color4: $color4,
  color5: $color5,
  color6: $color6,
  color7: $color7,
);

$colors-hover: (
  primary: $primary-hover,
  secondary: $secondary-hover,
  terciary: $terciary-hover,
  quinary: $quinary-hover,
  success: $success-hover,
  color1: $color1,
  color2: $color2,
  color3: $color3,
  color4: $color4,
  color5: $color5,
  color6: $color6,
  color7: $color7,
);

$colors-selected: (
  primary: $primary-selected,
  secondary: $secondary-selected,
  terciary: $terciary-selected,
  quinary: $quinary-selected,
  success: $success-selected,
  color1: $color1,
  color2: $color2,
  color3: $color3,
  color4: $color4,
  color5: $color5,
  color6: $color6,
  color7: $color7,
);

$colors-disabled: (
  primary: $primary-disabled,
  secondary: $secondary-disabled,
  terciary: $terciary-disabled,
  quinary: $quinary-disabled,
  success: $success-disabled,
  color1: $color1,
  color2: $color2,
  color3: $color3,
  color4: $color4,
  color5: $color5,
  color6: $color6,
  color7: $color7,
);

@mixin variantColors($outlined: false) {
  @each $var in $list {
    @if $outlined {
      &.is-#{$var}-outlined {
        border-color: map-get($colors, $var);
        color: map-get($colors, $var);
        background-color: transparent;
        &:hover {
          border-color: map-get($colors-hover, $var);
        }
        &:focus,
        &:active {
          border-color: map-get($colors-selected, $var);
        }
      }
    }
    &.is-#{$var} {
      background-color: map-get($colors, $var);
      border-color: map-get($colors, $var);
      color: white;
      &:hover {
        background-color: map-get($colors-hover, $var);
      }
      &:focus,
      &:active {
        background-color: map-get($colors-selected, $var);
      }
      &:disabled {
        background-color: map-get($colors-disabled, $var);
      }
    }
  }
}
.pw-tab-strip {
  &--is-vertical {
    display: flex;
    &.pw-tab-strip {
      > .pw-tab-strip__header {
        width: 20%;
        max-width: 200px;
        padding-right: 12px;
        .tab-strip__items {
          border-bottom: none !important;
          li {
            display: block;
            font-size: 18px;
            line-height: 1.33;
            padding: 10px 16px;
            margin-right: 12px;
            margin-left: 12px;

            &.is-active {
              background-color: #dee8ff;
              color: #5185fe;
              border-radius: 3px;
              opacity: 1 !important;
            }
            &:after {
              display: none !important;
            }
          }
        }
      }
      & > .pw-tab-strip__body {
        padding-left: 12px;
        width: 100%;
      }
    }
  }
  &--pill-mode {
    &.pw-tab-strip {
      > .pw-tab-strip__header {
        padding-right: 12px;
        .tab-strip__items {
          border-bottom: none !important;
          li {
            font-size: 14px;
            line-height: 1.33;
            padding: 3px 12px;
            color: #a4afb7;
            border: solid #a4afb7 1px;
            margin-right: 12px;
            margin-bottom: 3px;
            border-radius: 15px;
            &.is-active {
              color: #fff;
              opacity: 1 !important;
              &.is-primary {
                background-color: $primary;
                border-color: $primary;
              }
              &.is-secondary {
                background-color: $secondary;
                border-color: $secondary;
              }
              &.is-terciary {
                background-color: $terciary;
                border-color: $terciary;
              }
              &.is-quinary {
                background-color: $quinary;
                border-color: $quinary;
              }
              &.is-success {
                background-color: $success;
                border-color: $success;
              }
            }
            &:after {
              display: none !important;
            }
          }
        }
      }
      & > .pw-tab-strip__body {
        width: 100%;
      }
    }
  }
}
.tab-strip__items {
  list-style: none;
  padding: 0;
  border-bottom: 1px solid #d8e3eb;
  li {
    display: inline-block;
    padding-right: 12px;
    padding-left: 12px;
    padding-bottom: 8px;
    line-height: 1.38;
    color: #333;
    position: relative;
    cursor: pointer;
    &.is-disabled {
      color: #333;
      opacity: 0.5;
      pointer-events: none !important;
      &:after {
        background-color: #333;
        opacity: 0.5;
      }
    }
    &.is-filled {
      color: #333;
      &::after {
        opacity: 1;
        display: block;
      }
    }
    &.is-active {
      opacity: 1 !important;
      color: #5185fe;
      &.is-primary {
        color: $primary;
      }
      &.is-secondary {
        color: $secondary;
      }
      &.is-terciary {
        color: $terciary;
      }
      &.is-quinary {
        color: $quinary;
      }
      &.is-success {
        color: $success;
      }
      &::after {
        opacity: 1;
        display: block;
      }
    }
    &::after {
      width: 100%;
      position: absolute;
      height: 3px;
      left: 0;
      bottom: 0;
      background-color: #5185fe;
      content: '';
      display: none;
      transform: translateY(50%);
      border-radius: 3px;
    }
  }
}
</style>
